import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowRightIcon, RepeatIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { useFeatures } from '../../hooks/useFeatures';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { eyebrowCls, quietLink, sectionHeading } from '../../ui/sections';

/**
 * "Opbouwen": wat er gebeurt nádat je een protocol kiest. Het protocol zelf verkoopt het blok
 * Kiezen erboven (per klacht, met prijs); hier geen tweede rij protocolkaarten, maar de
 * belofte erachter: huidplan, bijsturen, opnieuw kijken. Als tijdlijn, want het is een volgorde;
 * de lijn loopt mee met het scrollen. Eronder het abonnement (sluit aan op "bestel bij").
 * De cadeaustaffel staat hier bewust níét: die geldt voor elke bestelling en staat daarom bij
 * "Liever met één product beginnen?" (HomeShelf).
 */
export function HomeProtocols() {
  const pages = usePages();
  const {
    subscriptions
  } = useFeatures();
  const reduce = useReducedMotion();
  const copy = pages.home.protocols;
  const lineRef = useRef<HTMLOListElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: lineRef,
    offset: ['start 75%', 'end 60%']
  });
  const milestones = copy.milestones.filter(m => m.title);
  return <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="home-protocols" data-id="element-7445" data-mp="3gao0cuemlida">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7446" data-mp="7v4am5d5yi2z">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-7447" data-mp="cr2xfoiufs1a">
          <div className="lg:col-span-5" data-id="element-7448" data-mp="3v6dds45qwn6e">
            {copy.eyebrow && <p className={`${eyebrowCls} text-clay`} data-id="element-7449" data-mp="3v5vcuu851ks5">{fill(copy.eyebrow)}</p>}
            <h2 id="home-protocols" className={`${copy.eyebrow ? 'mt-4' : ''} ${sectionHeading} text-ink`} data-id="element-7450" data-mp="31039kzzk81d">
              <HeadingText heading={copy.heading} data-id="element-7451" data-mp="qqcculz86i47" />
            </h2>
            {copy.body && <p className="mt-6 max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7452" data-mp="vjnhqhkh8oth">{fill(copy.body)}</p>}
            {copy.cta && <Link to="/routines" className={`mt-6 ${quietLink} text-ink`} data-id="element-7453" data-mp="15ci1ts65p96l">
                {fill(copy.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7454" data-mp="11b69hevwe9up" />
              </Link>}
          </div>

          {milestones.length > 0 && <ol ref={lineRef} className="relative lg:col-span-7 lg:col-start-6 lg:pl-4" data-id="element-7455" data-mp="rel3aq856spa">
              {/* De lijn van de tijdlijn; vult zich terwijl je leest. Bij "minder beweging" staat hij vol. */}
              <span aria-hidden="true" className="absolute bottom-3 left-[7px] top-3 w-px bg-ink/15 lg:left-[23px]" data-id="element-7456" data-mp="hnqgnkqzhr3b">
                <motion.span style={reduce ? undefined : {
              scaleY: scrollYProgress
            }} className="absolute inset-0 origin-top bg-clay" data-id="element-7457" data-mp="dl24b332a21l" />
              </span>
              {milestones.map((m, i) => <li key={`${i}-${m.title}`} className="relative pb-12 pl-10 last:pb-0 sm:pb-14" data-id="element-7458" data-mp="3r35oe2750ee" data-mpi={i}>
                  <span aria-hidden="true" className="absolute left-0 top-1.5 h-[15px] w-[15px] rounded-full border border-clay bg-sand lg:left-4" data-id="element-7459" data-mp="3uxw79j0wj1jg" />
                  <div className="lg:pl-6" data-id="element-7460" data-mp="3ppoeqqbrwjxw">
                    {m.when && <p className="text-[14px] text-clay" data-id="element-7461" data-mp="3upsv8gcoleo4">{fill(m.when)}</p>}
                    <h3 className="mt-1.5 type-title text-ink" data-id="element-7462" data-mp="3uqkj1501e83u">{fill(m.title)}</h3>
                    {m.body && <p className="mt-3 max-w-lg text-[17px] leading-relaxed text-muted" data-id="element-7463" data-mp="3iq39mp0r4cu">{fill(m.body)}</p>}
                  </div>
                </li>)}
            </ol>}
        </div>

        {/* Het abonnement als één regel: de uitleg zelf staat bij Protocollen en in de webshop. */}
        {subscriptions && <Link to="/routines" className="group mt-16 flex flex-col gap-2 border-t border-line pt-7 sm:flex-row sm:items-center sm:justify-between sm:gap-8" data-id="element-7464" data-mp="3gprk882pcqux">
            <span className="flex items-start gap-3 text-[17px] leading-relaxed text-ink" data-id="element-7465" data-mp="2jado4jqrd6fa">
              <RepeatIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7466" data-mp="2o85rqb0rmgzb" />
              <span data-id="element-7467" data-mp="ttruqfb233uu">
                <span className="type-name" data-id="element-7468" data-mp="tr5s8u30vejh">{pages.subscription.name}</span>
                <span className="text-muted" data-id="element-7469" data-mp="ow8hniq3gx4h"> · {pages.subscription.claim}</span>
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-2 pl-7 text-[15px] text-ink group-hover:underline group-hover:underline-offset-[6px] sm:pl-0" data-id="element-7470" data-mp="13l4q3gw2cc5m">
              Zo werkt het abonnement
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7471" data-mp="1cludbw79cu7q" />
            </span>
          </Link>}
      </div>
    </section>;
}